import { referenceDisplay } from '@seen/core/display'
import type { NewCommentInboxItem } from '@seen/core/models/inbox'
import {
  OrderCategory,
  orderCategoryLabel,
} from '@seen/core/models/orders/constants'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { createReference } from '@seen/core/references'
import { cn, IconComment } from '@seen/ui'
import type { ReactNode } from 'react'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { useCommentParentResource } from '#src/hooks/inbox/useCommentParentResource.ts'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useResource } from '#src/hooks/useResource.ts'
import { useTimelineItemUrl } from '#src/hooks/useTimelineItemUrl.ts'
import { InboxRow } from '#src/pages/inbox/row/InboxRow.tsx'
import { InboxRowAvatar } from '#src/pages/inbox/row/InboxRowAvatar.tsx'
import { InboxRowContent } from '#src/pages/inbox/row/InboxRowContent.tsx'
import { InboxRowIcon } from '#src/pages/inbox/row/InboxRowIcon.tsx'
import { InboxRowSubtext } from '#src/pages/inbox/row/InboxRowSubtext.tsx'
import { InboxRowText } from '#src/pages/inbox/row/InboxRowText.tsx'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

export interface NewCommentInboxItemRowProps {
  item: NewCommentInboxItem
  notificationItem: NotificationItem
  onSelect?: () => void
  isSelected?: boolean
  sidebarOpen?: boolean
}

export function NewCommentInboxItemRow({
  item,
  notificationItem,
  onSelect,
  isSelected,
  sidebarOpen,
}: NewCommentInboxItemRowProps): ReactNode {
  const {
    comment,
    parentResourceType,
    relatedTask,
    relatedOrder,
    patient,
    isLoading,
  } = useCommentParentResource(item.comment)
  const { data: practitioner } = useRead(
    ExtendedPractitioner,
    comment?.practitionerSender
  )

  const { data: partOf } = useResource(comment?.partOf?.[0])

  const hasRelatedTask = parentResourceType === 'Task' || !!relatedTask
  const hasRelatedOrder =
    parentResourceType === 'ServiceRequest' || !!relatedOrder

  const commentUrl = useTimelineItemUrl(comment)
  const patientName = patient?.name

  const orderTitle = getOrderTitle(relatedOrder)
  const categoryLabel =
    relatedOrder?.category && relatedOrder.category !== OrderCategory.Misc
      ? orderCategoryLabel(relatedOrder.category)
      : ''

  return (
    <InboxRow
      to={commentUrl}
      notificationItem={notificationItem}
      onSelect={onSelect}
      isSelected={isSelected}
      sidebarOpen={sidebarOpen}
    >
      <InboxRowAvatar
        profile={comment?.practitionerSender}
        icon={
          <InboxRowIcon>
            <IconComment className="size-[0.6875rem]" />
          </InboxRowIcon>
        }
      />
      <InboxRowContent>
        <InboxRowText
          notificationItem={notificationItem}
          isLoading={isLoading}
          badges={
            patient && (
              <ParticipantTaskRowBadge
                className="[&>button]:h-[1.55rem] [&>button]:py-0"
                participant={patient.reference}
              />
            )
          }
        >
          {hasRelatedTask && (
            <>
              {relatedTask?.priority && (
                <TaskPriorityIcon
                  priority={relatedTask.priority}
                  isTaskCompletedOrCancelled={
                    relatedTask.isCompletedOrCancelled
                  }
                  className={cn('-mt-0.75 mr-0.5 inline')}
                />
              )}
              {relatedTask?.state && (
                <IconTaskState
                  state={relatedTask.state}
                  className="-mt-0.75 mr-0.75 inline"
                />
              )}
              {relatedTask?.title ?? '[deleted]'}
            </>
          )}
          {hasRelatedOrder && (
            <>
              <span className={cn('relative top-0.5 inline-flex')}>
                <IconOrderStatus
                  status={relatedOrder?.status}
                  isSubmitting={relatedOrder?.isSubmitting}
                  className="size-4"
                />
              </span>{' '}
              <span className="text-foreground-secondary">
                {relatedOrder?.seenIdentifier}
              </span>{' '}
              {!!categoryLabel && <span>{categoryLabel}</span>}
              {!!categoryLabel && !!orderTitle && <span>: </span>}
              {!!orderTitle && <span>{orderTitle}</span>}
            </>
          )}
          {!hasRelatedTask &&
            !hasRelatedOrder &&
            extractTextFromHTML(comment?.message ?? '[deleted comment]')}
        </InboxRowText>
        <InboxRowSubtext>
          {!!practitioner && (
            <>
              {practitioner?.name}
              {!!practitioner?.roles?.length && (
                <> {practitioner?.roles?.join(', ')}</>
              )}{' '}
              {!relatedTask && !relatedOrder && (
                <>
                  commented on{' '}
                  {referenceDisplay(partOf && createReference(partOf))}
                </>
              )}
              {!!relatedTask && (
                <>
                  commented:{' '}
                  {extractTextFromHTML(comment?.message ?? '[deleted comment]')}
                </>
              )}
              {!!relatedOrder && (
                <>
                  commented:{' '}
                  {extractTextFromHTML(comment?.message ?? '[deleted comment]')}
                </>
              )}
            </>
          )}
          {!practitioner && (
            <>
              {!relatedTask && !relatedOrder && (
                <>
                  Comment on{' '}
                  {referenceDisplay(partOf && createReference(partOf))}
                </>
              )}
              {(!!relatedTask || !!relatedOrder) && (
                <>
                  Comment:{' '}
                  {extractTextFromHTML(comment?.message ?? '[deleted comment]')}
                </>
              )}
            </>
          )}
          {!hasRelatedOrder && patientName && ` • ${patientName}`}
        </InboxRowSubtext>
      </InboxRowContent>
    </InboxRow>
  )
}
